import { AggregatedArticle } from "@coworker/app/src/components/RoomSettings/hooks/useAggregatedArticles";
import { Article } from "@coworker/app/src/components/RoomSettings/types/article";
import { Media } from "@coworker/app/src/components/RoomSettings/types/media";
import { ItemDelete } from "@coworker/apprestructured/src/media-maintenance/components/item-cards/ItemDelete/ItemDelete";
import { ItemQuantityStepper } from "@coworker/apprestructured/src/media-maintenance/components/item-cards/ItemQuantityStepper/ItemQuantityStepper";
import styles from "@coworker/apprestructured/src/media-maintenance/styles/common-media-styles.module.css";
import { MediaItemViewType } from "@coworker/apprestructured/src/media-maintenance/types/mediaItemViewType";
import { ItemCard } from "@coworker/apprestructured/src/shared/components/item-cards/ItemCard/ItemCard";
import { ItemImage } from "@coworker/apprestructured/src/shared/components/item-cards/ItemImage/ItemImage";
import { ItemName } from "@coworker/apprestructured/src/shared/components/item-cards/ItemName/ItemName";
import { ItemNo } from "@coworker/apprestructured/src/shared/components/item-cards/ItemNo/ItemNo";
import { ItemStockInfo } from "@coworker/apprestructured/src/shared/hooks/item/useItemStock";
import { ItemBadge } from "@coworker/apprestructured/src/shared/types/ItemBadge";
import { ItemCardRenderType } from "@coworker/apprestructured/src/shared/types/itemCardRenderType";
import FixaLink from "@coworker/apprestructured/src/shared/wrappers/FixaLink/FixaLink";
import { formatProductIdWithDots } from "@coworker/reusable/formatters/product.formatter";
import { ItemStockAndEds } from "../ItemStockAndEds/ItemStockAndEds";

type MediaItemCardUnknownBaseProps<T extends AggregatedArticle | Article> = {
  itemNo: string;
  article: T;
  badges?: ItemBadge[];
  itemCardRenderType?: ItemCardRenderType;
  mediaItemViewType?: MediaItemViewType;
  stockInfo?: ItemStockInfo;
};

type MediaItemCardUnknownProps<T extends AggregatedArticle | Article> =
  | (MediaItemCardUnknownBaseProps<T> & { showActions?: false; media?: Media })
  | (MediaItemCardUnknownBaseProps<T> & { showActions: true; media: Media });

export const MediaItemCardUnknown = <T extends Article | AggregatedArticle>(
  props: MediaItemCardUnknownProps<T>
) => {
  const {
    itemNo,
    article,
    badges = [],
    showActions = false,
    itemCardRenderType = ItemCardRenderType.link,
    mediaItemViewType = MediaItemViewType.SIMPLE_ITEM_VIEW,
    stockInfo,
  } = props;
  const media = showActions ? props.media : undefined;
  const navigationUrl =
    mediaItemViewType === MediaItemViewType.AGGREGATED_ITEM_VIEW
      ? `/mediamaintenance/medias/${article.mediaId}/articles/${article.id}/product_article_id/${article.productArticleId}`
      : `/mediamaintenance/articles/${article.id}`;

  const itemContent = (
    <>
      <ItemImage imageUrl={""} />
      <div className={styles["item-card-root-column"]}>
        <ItemName name={"Unknown"} />
        <ItemNo itemNo={formatProductIdWithDots(itemNo)} />
        <ItemStockAndEds stockInfo={stockInfo} hfb={article.hfb} />
      </div>
    </>
  );

  return (
    <ItemCard badges={badges} dataTestId={"media-item-card-unknown"}>
      {itemCardRenderType === ItemCardRenderType.link ? (
        <FixaLink to={navigationUrl} className={styles["item-card-root-row"]}>
          {itemContent}
        </FixaLink>
      ) : (
        <div className={styles["item-card-root-row"]}>{itemContent}</div>
      )}

      {showActions && media && (
        <div className={styles["item-card-action-row"]}>
          <ItemQuantityStepper
            itemNo={itemNo}
            itemQuantityPieces={article?.nbrArticles ?? 0}
            article={article}
          />
          <ItemDelete
            itemNo={article.productArticleId}
            isItemInMultipleGroups={false}
            article={article}
            media={media}
          />
        </div>
      )}
    </ItemCard>
  );
};
